import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Donation Receipt Generator React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Donation Receipt Generator React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import { Dropdown, Avatar, Text, Grid, User } from "@nextui-org/react";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Donation Receipt Generator React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import axios from "axios";
import { getTrustDetailsPatch } from "Api";
import { userToken } from "Token";
import { getUserType } from "Token";
import { getAdminEmail } from "Token";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  // Logout Function
  const navigate = useNavigate()

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();

    if (userType === 'ADMIN') {
      navigate('/admin/login');
    }

    if (userType === 'TRUST') {
      navigate('/trust/login');
    }
  }

  // Variables
  const [trustDetail, setTrustDetail] = useState({})
  const [trustLogo, settrustLogo] = useState("https://i.pravatar.cc/150?u=a042581f4e29026704d")

  const token = userToken();
  const userType = getUserType();
  const adminEmail = getAdminEmail()

  // Get Trust Details
  const fetchTrustDetails = () => {
    axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    axios.patch(getTrustDetailsPatch)
      .then((res) => {
        if (res.data.user.TrustLogo !== "") {
          if(res.data.user.TrustLogo.includes('http://') || res.data.user.TrustLogo.includes('https://')) {
            settrustLogo(`${res.data.user.TrustLogo}`)
          } else {
            settrustLogo(`http://${res.data.user.TrustLogo}`)
          }
        }
        setTrustDetail(res.data.user)
      })
  }
  useEffect(() => {
    if (userType === "TRUST" && (token !== "" && token !== undefined)) {
      fetchTrustDetails()
    }
  }, [userType, token])

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar className="MainHeader" sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox className="navToggle" sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"} className="NavProfile">
              <Grid.Container justify="flex-start" gap={2}>
                <Grid>
                  <Dropdown placement="bottom-left">
                    <Dropdown.Trigger>
                      <Avatar bordered size="lg" as="button" color="secondary" src={trustLogo} />
                    </Dropdown.Trigger>
                    <Dropdown.Menu color="secondary" aria-label="Avatar Actions" >
                      <Dropdown.Item key="profile" css={{ height: "$20" }}>
                        <Text b size={16} color="inherit" css={{ d: "flex" }}>
                          Signed in as
                        </Text>
                        <Text b size={16} color="inherit" css={{ d: "flex" }}>
                          {userType === "TRUST" ? trustDetail.TrustEmail : adminEmail}
                        </Text>
                      </Dropdown.Item>
                      {
                        userType === "TRUST" ?
                          <Dropdown.Item key="profile" withDivider>
                            <NavLink size={16} to="/trust/profile">
                              <Text size={16} color="inherit" css={{ d: "flex" }}>
                                My Profile
                              </Text>
                            </NavLink>
                          </Dropdown.Item> :
                          userType === "ADMIN" ?
                            "" : ""
                      }
                      <Dropdown.Item size={16} key="logout" color="error" withDivider>
                        <Text size={16} color="inherit" css={{ d: "flex" }} onClick={(e) => handleLogout()}>
                          Logout
                        </Text>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Grid>
              </Grid.Container>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
